// AccountManager - Handles Firebase Authentication
// Supports anonymous sign-in with email/password account linking

import {
    signInAnonymously,
    linkWithCredential,
    signInWithEmailAndPassword,
    EmailAuthProvider,
    sendPasswordResetEmail,
    onAuthStateChanged,
    signOut,
    updateProfile
} from 'https://www.gstatic.com/firebasejs/10.7.1/firebase-auth.js';
import {
    doc,
    getDoc,
    setDoc,
    serverTimestamp
} from 'https://www.gstatic.com/firebasejs/10.7.1/firebase-firestore.js';
import { auth, db, isConfigured } from './config.js';

export class AccountManager {
    constructor() {
        this.currentUser = null;
        this.userDoc = null;
        this.friendCode = null;
        this.isReady = false;
        this.readyPromise = null;
        this.authStateListeners = [];

        if (isConfigured) {
            this.readyPromise = this._setupAuthStateListener();
        } else {
            this.readyPromise = Promise.resolve();
        }
    }

    /**
     * Wait for auth to be ready
     */
    async waitForReady() {
        await this.readyPromise;
        return this.isReady;
    }

    /**
     * Set up auth state listener
     */
    _setupAuthStateListener() {
        return new Promise((resolve) => {
            onAuthStateChanged(auth, async (user) => {
                this.currentUser = user;

                if (user) {
                    // Load or create user document
                    await this._ensureUserDocument(user);
                    console.log('[AccountManager] User signed in:', user.uid, user.isAnonymous ? '(anonymous)' : '(linked)');
                } else {
                    this.userDoc = null;
                    this.friendCode = null;
                    console.log('[AccountManager] User signed out');
                }

                // Notify listeners
                this.authStateListeners.forEach(callback => {
                    try {
                        callback({
                            user,
                            isAnonymous: user?.isAnonymous ?? true,
                            isLinked: this.isLinked(),
                            friendCode: this.friendCode
                        });
                    } catch (e) {
                        console.error('[AccountManager] Auth listener error:', e);
                    }
                });

                if (!this.isReady) {
                    this.isReady = true;
                    resolve();
                }
            });
        });
    }

    /**
     * Register a callback for auth state changes
     */
    onAuthStateChanged(callback) {
        this.authStateListeners.push(callback);

        // Immediately call with current state if ready
        if (this.isReady) {
            callback({
                user: this.currentUser,
                isAnonymous: this.currentUser?.isAnonymous ?? true,
                isLinked: this.isLinked(),
                friendCode: this.friendCode
            });
        }

        // Return unsubscribe function
        return () => {
            const index = this.authStateListeners.indexOf(callback);
            if (index > -1) {
                this.authStateListeners.splice(index, 1);
            }
        };
    }

    /**
     * Ensure user has a Firestore document
     * Only creates documents for linked (non-anonymous) users
     */
    async _ensureUserDocument(user) {
        if (!db) return;

        // Don't create documents for anonymous users - they don't need cloud features
        if (user.isAnonymous) {
            console.log('[AccountManager] Skipping user document for anonymous user');
            this.userDoc = null;
            this.friendCode = null;
            return;
        }

        const userRef = doc(db, 'users', user.uid);
        const userSnap = await getDoc(userRef);

        if (userSnap.exists()) {
            this.userDoc = userSnap.data();
            this.friendCode = this.userDoc.friendCode;

            // Check for missing fields and fix them
            const updateData = { lastSeen: serverTimestamp() };
            let needsUpdate = false;

            if (!this.friendCode) {
                console.log('[AccountManager] Existing user document missing friendCode, generating one');
                this.friendCode = this._generateFriendCode();
                updateData.friendCode = this.friendCode;
                this.userDoc.friendCode = this.friendCode;
                needsUpdate = true;
            }

            if (!this.userDoc.uid) {
                console.log('[AccountManager] Existing user document missing uid, adding it');
                updateData.uid = user.uid;
                this.userDoc.uid = user.uid;
                needsUpdate = true;
            }

            if (!this.userDoc.createdAt) {
                console.log('[AccountManager] Existing user document missing createdAt, adding it');
                updateData.createdAt = serverTimestamp();
                needsUpdate = true;
            }

            if (needsUpdate) {
                console.log('[AccountManager] Fixing incomplete user document');
            }

            await setDoc(userRef, updateData, { merge: true });
        } else {
            // Create new user document for linked user
            this.friendCode = this._generateFriendCode();
            this.userDoc = {
                uid: user.uid,
                displayName: user.displayName || null,
                email: user.email || null,
                photoURL: user.photoURL || null,
                friendCode: this.friendCode,
                createdAt: serverTimestamp(),
                lastSeen: serverTimestamp(),
                linkedProviders: user.providerData.map(p => p.providerId)
            };
            await setDoc(userRef, this.userDoc);
            console.log('[AccountManager] Created new user document for linked user');
        }
    }

    /**
     * Generate a unique 8-character friend code
     */
    _generateFriendCode() {
        const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // Removed confusing chars (0, O, 1, I)
        let code = '';
        for (let i = 0; i < 8; i++) {
            code += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        return code;
    }

    /**
     * Ensure user is signed in (auto anonymous if not)
     */
    async ensureSignedIn() {
        if (!isConfigured) {
            console.warn('[AccountManager] Firebase not configured');
            return null;
        }

        await this.waitForReady();

        if (this.currentUser) {
            return this.currentUser;
        }

        // Sign in anonymously
        try {
            const result = await signInAnonymously(auth);
            console.log('[AccountManager] Signed in anonymously');
            return result.user;
        } catch (error) {
            console.error('[AccountManager] Anonymous sign-in failed:', error);
            throw error;
        }
    }

    /**
     * Link anonymous account with email/password
     */
    async linkWithEmail(email, password) {
        if (!this.currentUser) {
            throw new Error('No user to link');
        }

        if (!this.currentUser.isAnonymous) {
            throw new Error('Account already linked');
        }

        try {
            const credential = EmailAuthProvider.credential(email, password);
            await linkWithCredential(this.currentUser, credential);

            // Update user document with email and ensure friendCode exists
            if (db) {
                const userRef = doc(db, 'users', this.currentUser.uid);
                const userSnap = await getDoc(userRef);

                // Generate friendCode if it doesn't exist
                if (!this.friendCode) {
                    this.friendCode = this._generateFriendCode();
                }

                const updateData = {
                    email: email,
                    linkedProviders: ['password'],
                    linkedAt: serverTimestamp()
                };

                // Include friendCode if document doesn't have one
                if (!userSnap.exists() || !userSnap.data()?.friendCode) {
                    updateData.friendCode = this.friendCode;
                    updateData.uid = this.currentUser.uid;
                    updateData.createdAt = serverTimestamp();
                }

                await setDoc(userRef, updateData, { merge: true });
            }

            console.log('[AccountManager] Account linked with email');
            return true;
        } catch (error) {
            console.error('[AccountManager] Link failed:', error);
            throw this._getReadableError(error);
        }
    }

    /**
     * Sign in with existing email/password account
     */
    async signInWithEmail(email, password) {
        if (!isConfigured) {
            throw new Error('Firebase not configured');
        }

        try {
            const result = await signInWithEmailAndPassword(auth, email, password);
            console.log('[AccountManager] Signed in with email');
            return result.user;
        } catch (error) {
            console.error('[AccountManager] Sign in failed:', error);
            throw this._getReadableError(error);
        }
    }

    /**
     * Send password reset email
     */
    async sendPasswordReset(email) {
        if (!isConfigured) {
            throw new Error('Firebase not configured');
        }

        try {
            await sendPasswordResetEmail(auth, email);
            console.log('[AccountManager] Password reset email sent');
            return true;
        } catch (error) {
            console.error('[AccountManager] Password reset failed:', error);
            throw this._getReadableError(error);
        }
    }

    /**
     * Sign out
     */
    async signOut() {
        if (auth) {
            await signOut(auth);
            console.log('[AccountManager] Signed out');
        }
    }

    /**
     * Convert Firebase error to readable message
     */
    _getReadableError(error) {
        const errorMessages = {
            'auth/email-already-in-use': 'This email is already registered. Try signing in instead.',
            'auth/invalid-email': 'Please enter a valid email address.',
            'auth/weak-password': 'Password must be at least 6 characters.',
            'auth/user-not-found': 'No account found with this email.',
            'auth/wrong-password': 'Incorrect email or password.',
            'auth/invalid-credential': 'Incorrect email or password.',
            'auth/invalid-login-credentials': 'Incorrect email or password.',
            'auth/too-many-requests': 'Too many attempts. Please try again later.',
            'auth/network-request-failed': 'Network error. Check your connection.',
            'auth/credential-already-in-use': 'This email is already linked to another account.',
            'auth/requires-recent-login': 'Please sign out and sign in again.',
            'auth/provider-already-linked': 'This account is already linked to an email.',
            'auth/account-exists-with-different-credential': 'An account already exists with this email.'
        };

        const message = errorMessages[error.code] || error.message || 'An error occurred';
        const readableError = new Error(message);
        readableError.code = error.code;
        return readableError;
    }

    // === Getters ===

    isLinked() {
        if (!this.currentUser) return false;
        return !this.currentUser.isAnonymous;
    }

    getUserId() {
        return this.currentUser?.uid ?? null;
    }

    getEmail() {
        return this.currentUser?.email ?? null;
    }

    getDisplayName() {
        return this.currentUser?.displayName ?? 'Anonymous Driver';
    }

    /**
     * Update display name
     * @param {string} displayName - New display name
     */
    async updateDisplayName(displayName) {
        if (!isConfigured || !this.currentUser) {
            throw new Error('Not signed in');
        }

        displayName = displayName.trim();
        if (!displayName || displayName.length < 1) {
            throw new Error('Display name cannot be empty');
        }
        if (displayName.length > 20) {
            throw new Error('Display name must be 20 characters or less');
        }

        // Update Firebase Auth profile
        await updateProfile(this.currentUser, { displayName });

        // Update Firestore user document
        const userRef = doc(db, 'users', this.currentUser.uid);
        await setDoc(userRef, { displayName }, { merge: true });

        console.log('[AccountManager] Display name updated:', displayName);
    }

    getFriendCode() {
        return this.friendCode;
    }

    isFirebaseConfigured() {
        return isConfigured;
    }
}
